/*
 * A page that arrives when it is asked for — and a build that changed under
 * an open page.
 *
 * 2026-10: the storefront shipped every page in one 3.5 MB script, so an ad
 * shopper opening one product on a phone downloaded the booking flow, the
 * courses, the stays and the customer's account before the buy button could
 * paint. The pages off the shopper's path are `lazyPage`s: a chunk each,
 * fetched on first navigation to them.
 *
 * A chunk is named by its content hash. When a deploy replaces the build while
 * someone has the old index.html open, the chunk that page asks for no longer
 * exists (the store's server answers 404 for a file not in the build, never the
 * page), and `import()` rejects. The page reloads itself once — the new
 * index.html names chunks that exist — and remembers that it did in
 * sessionStorage, so a chunk that is missing *again* reaches the error boundary
 * instead of reloading forever. A load that succeeds forgets the mark, so the
 * deploy after this one gets its one reload too.
 */
import { lazy } from 'react';

const RELOADED_KEY = 'stylon:chunk-reloaded';

/** webpack's ChunkLoadError, or the browser's own words for a dynamic import it could not fetch. */
export function isChunkLoadError(err) {
  const name = String(err?.name || '');
  const message = String(err?.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function reloadedAlready() {
  try { return window.sessionStorage.getItem(RELOADED_KEY) === '1'; } catch (_) { return true; }
}
function markReloaded() {
  try { window.sessionStorage.setItem(RELOADED_KEY, '1'); } catch (_) { /* then it counts as already reloaded */ }
}
function forgetReload() {
  try { window.sessionStorage.removeItem(RELOADED_KEY); } catch (_) { /* nothing to forget */ }
}

/** `React.lazy`, with the one reload a renamed chunk deserves. `load` is `() => import('./Pages/X')`. */
export function lazyPage(load) {
  return lazy(() =>
    load().then(
      (mod) => { forgetReload(); return mod; },
      (err) => {
        if (isChunkLoadError(err) && typeof window !== 'undefined' && !reloadedAlready()) {
          markReloaded();
          window.location.reload();
          return new Promise(() => {}); // the page is on its way out; nothing to render meanwhile
        }
        throw err;
      },
    ),
  );
}
